/**
 * 진화 기록의 두 보기(2026-11-04 사용자 요청 — 「이력처럼 기록이 남아야」 · 「기능 수정도 간략하게」).
 *
 *   · HistoryView — 배운 것이 생기고 · 켜지고 · 꺼지고 · 버려지고 · 글이 고쳐지고 · 지워진 기록(사용자별, 지우지 않는다).
 *     누가 했는지(직접 · 대화에서 · 가져옴 · 자동)를 함께 보인다. 수치만 바뀐 갱신은 서버가 남기지 않는다.
 *   · ChangelogView — 앱이 판마다 무엇이 바뀌었는지(모두에게 같은 읽기 전용 목록, backend experience/changelog.js).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { formatApiError } from '../../utils/formatters';
import { storedTime } from '../../utils/storedTime';
import * as api from '../../services/experience';

const PAGE = 200;

/** 한 줄의 「무엇이 일어났나」 — 상태 변화는 바뀐 뒤의 상태로 말한다(켬 · 끔 · 버림 …) */
function whatText(h, t) {
  if (h.kind === 'status') return t(`common:evo.hist.to.${h.to}`, { defaultValue: h.to || '' });
  if (h.kind === 'created') {
    if (h.to === 'proposed') return t('common:evo.hist.kind.proposed');
    // 직접 적은 선호·가져온 것은 「배운」 것이 아니다
    if (h.by === 'user' || h.by === 'import') return t(h.by === 'user' ? 'common:evo.hist.kind.added' : 'common:evo.hist.kind.importedItem');
    return t('common:evo.hist.kind.created');
  }
  return t(`common:evo.hist.kind.${h.kind}`, { defaultValue: h.kind });
}

/** 묶음 사건(가져오기·내보내기)의 한 줄 — 항목이 없으니 수치로 말한다 */
function eventText(h, t) {
  const d = h.data || {};
  if (h.kind === 'importMerged' || h.kind === 'importUndone') return t('common:evo.hist.importCounts', { prefs: d.prefs || 0, guides: d.guides || 0 });
  if (h.kind === 'exported') {
    const c = d.counts || {};
    return t('common:evo.hist.exportCounts', { count: Object.values(c).reduce((n, x) => n + (Number(x) || 0), 0) });
  }
  return '';
}

export function HistoryView() {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [more, setMore] = useState(false);

  const load = useCallback(async (before = null) => {
    setLoading(true);
    try {
      const r = await api.listHistory({ before, limit: PAGE });
      setRows(prev => (before ? [...prev, ...(r.items || [])] : (r.items || [])));
      setMore(!!r.more);
    } catch (err) { toast.error(formatApiError(err, t)); } finally { setLoading(false); }
  }, [t]);
  useEffect(() => { load(); }, [load]);

  // 날짜별로 묶는다 — 최근 날이 위
  const days = useMemo(() => {
    const out = [];
    for (const h of rows) {
      const day = storedTime(h.at, 'date');
      if (!out.length || out[out.length - 1].day !== day) out.push({ day, list: [] });
      out[out.length - 1].list.push(h);
    }
    return out;
  }, [rows]);

  if (loading && !rows.length) return <div className="exp-empty">{t('common:state.loading')}</div>;
  if (!rows.length) return <div className="exp-empty">{t('common:evo.hist.empty')}</div>;
  return (
    <div className="exp-hist">
      <div className="exp-intro">{t('common:evo.hist.intro')}</div>
      {days.map(d => (
        <section key={d.day} className="exp-hist-day">
          <h4 className="exp-groupTitle">{d.day} <span className="exp-count">{d.list.length}</span></h4>
          <ul className="exp-hist-list">
            {d.list.map(h => (
              <li key={h.id} className={`exp-hist-row exp-hist-${h.kind}${h.kind === 'status' ? ` exp-hist-to-${h.to}` : ''}`}>
                <span className="exp-hist-time">{storedTime(h.at, 'time')}</span>
                <span className="exp-hist-what">{whatText(h, t)}</span>
                <span className="exp-hist-title">
                  {h.title || eventText(h, t)}
                  {h.itemKind && h.section !== 'userPref' ? <span className="exp-hist-sub"> · {t(`common:evo.hist.item.${h.itemKind}`, { defaultValue: h.itemKind })}</span> : null}
                  {h.note ? <span className="exp-hist-note">{h.note}</span> : null}
                </span>
                <span className={`exp-chip exp-hist-by exp-hist-by-${h.by}`}>{t(`common:evo.hist.by.${h.by}`, { defaultValue: h.by })}</span>
              </li>
            ))}
          </ul>
        </section>
      ))}
      {more ? (
        <button type="button" className="app-btn exp-hist-more" disabled={loading} onClick={() => load(rows[rows.length - 1].id)}>
          {t('common:evo.hist.more')}
        </button>
      ) : null}
    </div>
  );
}

export function ChangelogView() {
  const { t, i18n } = useTranslation();
  const [groups, setGroups] = useState(null);
  useEffect(() => {
    let alive = true;
    api.changelog(i18n.language).then(r => { if (alive) setGroups(r.groups || []); }).catch((err) => { if (alive) { setGroups([]); toast.error(formatApiError(err, t)); } });
    return () => { alive = false; };
  }, [i18n.language, t]);
  if (!groups) return <div className="exp-empty">{t('common:state.loading')}</div>;
  if (!groups.length) return <div className="exp-empty">{t('common:evo.changes.empty')}</div>;
  return (
    <div className="exp-hist">
      <div className="exp-intro">{t('common:evo.changes.intro')}</div>
      {groups.map(g => (
        <section key={`${g.version}-${g.date}`} className="exp-hist-day">
          <h4 className="exp-groupTitle">v{g.version} <span className="exp-count">{g.date}</span></h4>
          <ul className="exp-changes">
            {g.items.map(line => <li key={line}>{line}</li>)}
          </ul>
        </section>
      ))}
    </div>
  );
}

